import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Paper, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Chip, Alert, LinearProgress, IconButton, Tooltip, Button, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Collapse, Card, CardMedia, Snackbar,
  useMediaQuery, useTheme, Stack, Divider, Badge
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import HistoryIcon from '@mui/icons-material/History';
import SpeedIcon from '@mui/icons-material/Speed';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';

import { meterReadingsApi } from '../../services/api';
import { formatNuf } from '../../utils/formatNuf';
import { getApiBaseUrl } from '../../utils/runtimeUrls';
import CircularProgress from '@mui/material/CircularProgress';

// Haversine distance calculation (meters)
const getDistanceMeters = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const MC_TO_LITERS = 4.15;

const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return parseFloat(num).toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('it-IT');
};

const formatDateTime = (date) => {
  if (!date) return '-';
  // PostgreSQL may return timestamps without Z suffix - ensure UTC parsing
  let d = new Date(date);
  if (typeof date === 'string' && !date.endsWith('Z') && !date.includes('+')) {
    d = new Date(date.replace(' ', 'T') + 'Z');
  }
  return d.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const isOneOrTwoMcConsumption = (consumoMc) => {
  const consumo = Number(consumoMc);
  return Number.isFinite(consumo) && (consumo === 1 || consumo === 2);
};

const getStatoColor = (stato) => {
  switch (stato) {
    case 'inserita': return 'default';
    case 'verificata': return 'info';
    case 'fatturata': return 'success';
    case 'annullata': return 'error';
    case 'mista': return 'warning';
    default: return 'default';
  }
};

const API_BASE = getApiBaseUrl();

const getSessionSource = (session) => session?.source || session?.inserted_from || 'pwa';
const getSourceLabel = (source) => source === 'import' ? 'Import Excel' : 'Mobile';
const getSourceIcon = (source) => source === 'import' ? <UploadFileIcon /> : <PhoneAndroidIcon />;

// ============================================================
// MAIN COMPONENT
// ============================================================
const MobileReadingsTab = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  // View state: null = list, object = detail
  const [selectedSession, setSelectedSession] = useState(null);

  return (
    <Box>
      {selectedSession ? (
        <SessionDetailView
          session={selectedSession}
          onBack={() => setSelectedSession(null)}
          isMobile={isMobile}
          queryClient={queryClient}
        />
      ) : (
        <SessionListView
          onSelectSession={setSelectedSession}
          isMobile={isMobile}
        />
      )}
    </Box>
  );
};

// ============================================================
// SESSION LIST VIEW
// ============================================================
const SessionListView = ({ onSelectSession, isMobile }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showArchived, setShowArchived] = useState(false);
  const [filters, setFilters] = useState({
    data_inizio: '',
    data_fine: '',
    operatore: '',
    stato: '',
    source: ''
  });

  const sessionsQuery = useQuery({
    queryKey: ['mobile-sessions', filters, page, rowsPerPage, showArchived],
    queryFn: () => meterReadingsApi.getMobileSessions({
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
      page: page + 1,
      limit: rowsPerPage,
      ...(showArchived ? { show_archived: '1' } : {})
    }).then(res => res.data)
  });

  const sessions = sessionsQuery.data?.data || [];
  const operators = sessionsQuery.data?.operators || [];
  const pagination = sessionsQuery.data?.pagination || {};

  return (
    <Box>
      {/* Filtri */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth size="small" type="date" label="Da"
              InputLabelProps={{ shrink: true }}
              value={filters.data_inizio}
              onChange={e => { setFilters(f => ({ ...f, data_inizio: e.target.value })); setPage(0); }}
            />
          </Grid>
          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth size="small" type="date" label="A"
              InputLabelProps={{ shrink: true }}
              value={filters.data_fine}
              onChange={e => { setFilters(f => ({ ...f, data_fine: e.target.value })); setPage(0); }}
            />
          </Grid>
          <Grid item xs={12} sm={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Letturista</InputLabel>
              <Select
                value={filters.operatore} label="Letturista"
                onChange={e => { setFilters(f => ({ ...f, operatore: e.target.value })); setPage(0); }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {operators.map(op => (
                  <MenuItem key={op} value={op}>{op}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2.2}>
            <FormControl fullWidth size="small">
              <InputLabel>Stato</InputLabel>
              <Select
                value={filters.stato} label="Stato"
                onChange={e => { setFilters(f => ({ ...f, stato: e.target.value })); setPage(0); }}
              >
                <MenuItem value="">Tutti</MenuItem>
                <MenuItem value="inserita">Da convalidare</MenuItem>
                <MenuItem value="verificata">Convalidate</MenuItem>
                <MenuItem value="fatturata">Fatturate</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Origine</InputLabel>
              <Select
                value={filters.source} label="Origine"
                onChange={e => { setFilters(f => ({ ...f, source: e.target.value })); setPage(0); }}
              >
                <MenuItem value="">Tutte</MenuItem>
                <MenuItem value="pwa">Mobile</MenuItem>
                <MenuItem value="import">Import Excel</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={1.4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showArchived}
                  onChange={e => { setShowArchived(e.target.checked); setPage(0); }}
                />
              }
              label={<Typography variant="caption">Archiviate</Typography>}
              sx={{ mr: 0 }}
            />
            <Tooltip title="Aggiorna">
              <IconButton onClick={() => sessionsQuery.refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {sessionsQuery.isLoading && <LinearProgress sx={{ mb: 2 }} />}
      {sessionsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Errore nel caricamento: {sessionsQuery.error?.message}
        </Alert>
      )}

      {/* Nessuna sessione */}
      {!sessionsQuery.isLoading && sessions.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nessuna sessione trovata. Le letture importate o raccolte da mobile appariranno qui.
        </Alert>
      )}

      {/* Tabella sessioni */}
      {sessions.length > 0 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Impianto</TableCell>
                  {!isMobile && <TableCell>Indirizzo</TableCell>}
                  <TableCell>Data</TableCell>
                  <TableCell>Letturista</TableCell>
                  <TableCell align="center">Letture</TableCell>
                  {!isMobile && <TableCell align="center">Livello</TableCell>}
                  {!isMobile && <TableCell align="center">Foto</TableCell>}
                  {!isMobile && <TableCell align="center">Anomalie</TableCell>}
                  <TableCell>Stato</TableCell>
                  <TableCell align="center">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session, idx) => (
                  <TableRow
                    key={`${session.plant_id}-${session.data_lettura}-${session.operatore}-${getSessionSource(session)}-${idx}`}
                    hover
                    sx={{ cursor: 'pointer', ...(session.is_archived ? { opacity: 0.5, bgcolor: 'action.hover' } : {}) }}
                    onClick={() => onSelectSession(session)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {session.plant?.plant_code || session.plant_id}
                      </Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                          {session.plant?.indirizzo || '-'}{session.plant?.citta ? `, ${session.plant.citta}` : ''}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2">{formatDate(session.data_lettura)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getSourceIcon(getSessionSource(session))}
                        label={session.operatore || '-'}
                        size="small" variant="outlined"
                      />
                      {!isMobile && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {getSourceLabel(getSessionSource(session))}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={session.readings_count} size="small" color="primary" />
                    </TableCell>
                    {!isMobile && (
                      <TableCell align="center">
                        {session.livello_percent !== null && session.livello_percent !== undefined ? (
                          <Tooltip title={session.livello_litri ? `${formatNumber(session.livello_litri, 0)} L` : ''}>
                            <Chip
                              icon={<PropaneTankIcon />}
                              label={`${formatNumber(session.livello_percent, 0)}%`}
                              size="small"
                              color={session.livello_percent <= 20 ? 'error' : session.livello_percent <= 40 ? 'warning' : 'success'}
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell align="center">
                        {session.photos_count > 0 ? (
                          <Badge badgeContent={session.photos_count} color="secondary">
                            <CameraAltIcon fontSize="small" color="action" />
                          </Badge>
                        ) : '-'}
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell align="center">
                        {session.has_anomaly ? (
                          <Tooltip title="Consumo anomalo rilevato">
                            <WarningIcon color="warning" fontSize="small" />
                          </Tooltip>
                        ) : (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={session.stato_riepilogo}
                          size="small"
                          color={getStatoColor(session.stato_riepilogo)}
                        />
                        {session.is_archived && (
                          <Chip icon={<ArchiveIcon />} label="Archiviata" size="small" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); onSelectSession(session); }}>
                        Apri
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Righe"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
          />
        </Paper>
      )}
    </Box>
  );
};

// ============================================================
// SESSION DETAIL VIEW
// ============================================================
const SessionDetailView = ({ session, onBack, isMobile, queryClient }) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editDialog, setEditDialog] = useState({ open: false, reading: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, reading: null });
  const [photoDialog, setPhotoDialog] = useState({ open: false, photos: [], index: 0 });

  // Fetch session detail
  const detailQuery = useQuery({
    queryKey: ['mobile-session-detail', session.plant_id, session.data_lettura, session.operatore, getSessionSource(session)],
    queryFn: () => meterReadingsApi.getMobileSessionDetail({
      plant_id: session.plant_id,
      data_lettura: session.data_lettura,
      operatore: session.operatore,
      source: getSessionSource(session)
    }).then(res => res.data?.data)
  });

  const detail = detailQuery.data;
  const readings = useMemo(() => detail?.readings || [], [detail?.readings]);
  const invalidateSessionQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['mobile-session-detail'] });
    queryClient.invalidateQueries({ queryKey: ['mobile-sessions'] });
  };

  // Group readings by meter, most recent first within each group, groups sorted by most recent
  const groupedByMeter = useMemo(() => {
    const groups = {};
    for (const r of readings) {
      const key = r.meter_id || r.id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    Object.values(groups).forEach(g => g.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    return Object.values(groups).sort((a, b) => new Date(b[0].created_at) - new Date(a[0].created_at));
  }, [readings]);

  // Validate single
  const validateMutation = useMutation({
    mutationFn: (id) => meterReadingsApi.update(id, { stato: 'verificata' }),
    onSuccess: () => {
      invalidateSessionQueries();
      setSnackbar({ open: true, message: 'Lettura convalidata', severity: 'success' });
    },
    onError: (err) => setSnackbar({ open: true, message: err.response?.data?.message || 'Errore', severity: 'error' })
  });

  // Validate batch
  const validateBatchMutation = useMutation({
    mutationFn: (ids) => meterReadingsApi.validateBatch({ reading_ids: ids }),
    onSuccess: (res) => {
      invalidateSessionQueries();
      const count = res.data?.data?.validated_count || 0;
      setSnackbar({ open: true, message: `${count} letture convalidate`, severity: 'success' });
    },
    onError: (err) => setSnackbar({ open: true, message: err.response?.data?.message || 'Errore', severity: 'error' })
  });

  // Delete (annulla)
  const deleteMutation = useMutation({
    mutationFn: (id) => meterReadingsApi.delete(id),
    onSuccess: () => {
      invalidateSessionQueries();
      setDeleteDialog({ open: false, reading: null });
      setSnackbar({ open: true, message: 'Lettura annullata', severity: 'success' });
    },
    onError: (err) => setSnackbar({ open: true, message: err.response?.data?.message || 'Errore', severity: 'error' })
  });

  // Edit
  const editMutation = useMutation({
    mutationFn: ({ id, data }) => meterReadingsApi.update(id, data),
    onSuccess: () => {
      invalidateSessionQueries();
      setEditDialog({ open: false, reading: null });
      setSnackbar({ open: true, message: 'Lettura modificata', severity: 'success' });
    },
    onError: (err) => setSnackbar({ open: true, message: err.response?.data?.message || 'Errore', severity: 'error' })
  });

  // Archive / Unarchive session
  const archiveMutation = useMutation({
    mutationFn: (archived) => meterReadingsApi.archiveSession({
      plant_id: session.plant_id,
      data_lettura: session.data_lettura,
      operatore: session.operatore,
      source: getSessionSource(session),
      archived
    }),
    onSuccess: (res) => {
      invalidateSessionQueries();
      const archived = res.data?.data?.archived;
      setSnackbar({ open: true, message: archived ? 'Sessione archiviata' : 'Sessione ripristinata', severity: 'success' });
      if (archived) onBack();
    },
    onError: (err) => setSnackbar({ open: true, message: err.response?.data?.message || 'Errore', severity: 'error' })
  });

  // Counts
  const insertedIds = readings.filter(r => r.stato === 'inserita').map(r => r.id);
  const allValidated = insertedIds.length === 0 && readings.length > 0;

  const handleValidateAll = () => {
    if (insertedIds.length > 0) {
      validateBatchMutation.mutate(insertedIds);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          <IconButton onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold">
              {detail?.plant?.plant_code || session.plant?.plant_code || '...'}
              {' — '}
              {detail?.plant?.indirizzo || session.plant?.indirizzo || ''}
              {detail?.plant?.citta ? `, ${detail.plant.citta}` : ''}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
              <Chip icon={<AccessTimeIcon />} label={formatDate(session.data_lettura)} size="small" />
              <Chip icon={getSourceIcon(detail?.source || getSessionSource(session))} label={`${getSourceLabel(detail?.source || getSessionSource(session))}: ${detail?.operatore || session.operatore || '-'}`} size="small" variant="outlined" />
              <Chip label={`${readings.length} letture`} size="small" color="primary" />
              {detail?.livello && (
                <Chip
                  icon={<PropaneTankIcon />}
                  label={`Livello: ${formatNumber(detail.livello.percent, 0)}%${detail.livello.litri ? ` (${formatNumber(detail.livello.litri, 0)} L)` : ''}`}
                  size="small"
                  color={detail.livello.percent <= 20 ? 'error' : detail.livello.percent <= 40 ? 'warning' : 'success'}
                />
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Aggiorna">
              <IconButton onClick={() => detailQuery.refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {insertedIds.length > 0 && (
              <Button
                variant="contained" color="success"
                startIcon={<DoneAllIcon />}
                onClick={handleValidateAll}
                disabled={validateBatchMutation.isPending}
              >
                {isMobile ? 'Convalida' : `Convalida tutte (${insertedIds.length})`}
              </Button>
            )}
            {allValidated && (
              <Chip icon={<CheckCircleIcon />} label="Tutte convalidate" color="success" />
            )}
            <Tooltip title={session.is_archived ? 'Ripristina sessione' : 'Archivia sessione'}>
              <Button
                variant="outlined"
                color={session.is_archived ? 'primary' : 'inherit'}
                startIcon={session.is_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                onClick={() => archiveMutation.mutate(!session.is_archived)}
                disabled={archiveMutation.isPending}
                size="small"
              >
                {session.is_archived ? 'Ripristina' : 'Archivia'}
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {detailQuery.isLoading && <LinearProgress sx={{ mb: 2 }} />}
      {detailQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Errore: {detailQuery.error?.message}
        </Alert>
      )}

      {/* Tank Level Card */}
      {detail?.livello && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <PropaneTankIcon color={detail.livello.percent <= 20 ? 'error' : detail.livello.percent <= 40 ? 'warning' : 'success'} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Livello Serbatoio Rilevato</Typography>
              <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap">
                <Typography variant="h5" fontWeight="bold" color={detail.livello.percent <= 20 ? 'error.main' : detail.livello.percent <= 40 ? 'warning.main' : 'success.main'}>
                  {formatNumber(detail.livello.percent, 0)}%
                </Typography>
                {detail.livello.litri != null && (
                  <Typography variant="body1">
                    {formatNumber(detail.livello.litri, 0)} litri
                    {detail.capacita_totale ? ` / ${formatNumber(detail.capacita_totale, 0)} L` : ''}
                  </Typography>
                )}
                {detail.livello.variazione_litri != null && detail.livello.variazione_litri !== 0 && (
                  <Typography variant="body2" color={detail.livello.variazione_litri > 0 ? 'success.main' : 'text.secondary'}>
                    (variazione: {detail.livello.variazione_litri > 0 ? '+' : ''}{formatNumber(detail.livello.variazione_litri, 0)} L)
                  </Typography>
                )}
              </Stack>
            </Box>
            {detail.livello.percent != null && (
              <Box sx={{ width: isMobile ? '100%' : 200 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, detail.livello.percent))}
                  color={detail.livello.percent <= 20 ? 'error' : detail.livello.percent <= 40 ? 'warning' : 'success'}
                  sx={{ height: 12, borderRadius: 1 }}
                />
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {/* Plant-level photos with thumbnails + date/time */}
      {detail?.plant_photos?.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            <PhotoLibraryIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            Foto impianto ({detail.plant_photos.length})
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {detail.plant_photos.map((photo, idx) => {
              const plantLat = detail?.plant?.latitude;
              const plantLng = detail?.plant?.longitude;
              const dist = (photo.gps_latitude && photo.gps_longitude && plantLat && plantLng)
                ? getDistanceMeters(parseFloat(photo.gps_latitude), parseFloat(photo.gps_longitude), parseFloat(plantLat), parseFloat(plantLng))
                : null;
              const distLabel = dist != null ? (dist < 1000 ? `${Math.round(dist)} m` : `${(dist / 1000).toFixed(1)} km`) : null;
              return (
                <Card
                  key={photo.id}
                  sx={{ width: 200, cursor: 'pointer', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}
                  onClick={() => setPhotoDialog({ open: true, photos: detail.plant_photos, index: idx })}
                >
                  <CardMedia
                    component="img"
                    height="130"
                    image={`${API_BASE}/meter-readings/photo/${photo.filename}`}
                    alt={photo.original_name || 'Foto'}
                    sx={{ objectFit: 'cover' }}
                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="130"><rect fill="%23eee" width="200" height="130"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999" font-size="12">Foto</text></svg>'; }}
                  />
                  <Box sx={{ px: 1, py: 0.5, bgcolor: 'grey.50' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(photo.created_at)}
                      </Typography>
                    </Stack>
                    {(photo.gps_latitude && photo.gps_longitude) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <LocationOnIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                          {parseFloat(photo.gps_latitude).toFixed(5)}, {parseFloat(photo.gps_longitude).toFixed(5)}
                          {' '}
                          <a href={`https://maps.google.com/?q=${photo.gps_latitude},${photo.gps_longitude}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', fontSize: 10 }} onClick={(e) => e.stopPropagation()}>Maps</a>
                        </Typography>
                        {distLabel && (
                          <Chip label={`↔ ${distLabel}`} size="small" sx={{ height: 18, fontSize: 10, ml: 0.5 }} color={dist > 5000 ? 'warning' : 'default'} />
                        )}
                        {photo.gps_accuracy && (
                          <Chip label={`GPS ±${Math.round(parseFloat(photo.gps_accuracy))}m`} size="small" sx={{ height: 18, fontSize: 10, ml: 0.5 }} color={parseFloat(photo.gps_accuracy) <= 10 ? 'success' : parseFloat(photo.gps_accuracy) <= 50 ? 'warning' : 'error'} />
                        )}
                      </Box>
                    )}
                  </Box>
                </Card>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Readings table - grouped by meter, most recent first */}
      {readings.length > 0 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 50 }}>
                    <Tooltip title="Storico letture">
                      <HistoryIcon fontSize="small" sx={{ color: 'white' }} />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 70 }}>Progr.</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Contatore</TableCell>
                  {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cliente</TableCell>}
                  {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Insoluto</TableCell>}
                  {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Ultima Lettura</TableCell>}
                  {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Media Bim.</TableCell>}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nuova Lettura</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Data/Ora</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Foto</TableCell>
                  {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Fatt.</TableCell>}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Consumo</TableCell>
                  {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Importo</TableCell>}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Stato</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 130 }} align="center">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedByMeter.map(group => (
                  <ReadingRow
                    key={group[0].meter_id || group[0].id}
                    readingGroup={group}
                    isMobile={isMobile}
                    onValidate={(id) => validateMutation.mutate(id)}
                    onEdit={(r) => setEditDialog({ open: true, reading: r })}
                    onDelete={(r) => setDeleteDialog({ open: true, reading: r })}
                    onPhotoClick={(photos, idx) => setPhotoDialog({ open: true, photos, index: idx })}
                    validatePending={validateMutation.isPending}
                    queryClient={queryClient}
                    setSnackbar={setSnackbar}
                    plantData={detail?.plant}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Edit Dialog */}
      <EditReadingDialog
        open={editDialog.open}
        reading={editDialog.reading}
        onClose={() => setEditDialog({ open: false, reading: null })}
        onSave={(id, data) => editMutation.mutate({ id, data })}
        saving={editMutation.isPending}
        isMobile={isMobile}
      />

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, reading: null })} fullScreen={isMobile}>
        <DialogTitle>Conferma Annullamento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Annullare la lettura del contatore <strong>{deleteDialog.reading?.meter?.meter_number}</strong>?
            <br />La lettura verrà marcata come "annullata" e non sarà più disponibile per la fatturazione.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, reading: null })} color="inherit">Annulla</Button>
          <Button
            onClick={() => deleteMutation.mutate(deleteDialog.reading?.id)}
            color="error" variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Annullamento...' : 'Annulla lettura'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Lightbox */}
      <PhotoDialog
        open={photoDialog.open}
        photos={photoDialog.photos}
        initialIndex={photoDialog.index}
        onClose={() => setPhotoDialog({ open: false, photos: [], index: 0 })}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ============================================================
// READING ROW (ReadingEntryTab-style with inline edit)
// ============================================================
const ReadingRow = ({ readingGroup, isMobile, onValidate, onEdit, onDelete, onPhotoClick, validatePending, queryClient, setSnackbar, plantData }) => {
  const [expanded, setExpanded] = useState(false);
  const [editValue, setEditValue] = useState(null); // null = not editing
  const prevValueRef = useRef(null);

  const reading = readingGroup[0]; // Most recent
  const olderReadings = readingGroup.slice(1);
  const hasMultiple = olderReadings.length > 0;

  const photos = reading.photos || [];
  const consumoMc = reading.consumo_mc;
  const consumoLitri = reading.consumo_litri;
  const isNegative = reading.is_negative;
  const isOneOrTwoMc = isOneOrTwoMcConsumption(consumoMc);
  const isAnomaly = reading.computed_anomaly || reading.is_anomaly;

  const importoStimato = useMemo(() => {
    if (consumoLitri === null || consumoLitri === undefined) return null;
    const prezzo = parseFloat(reading.meter?.prezzo_litro || reading.prezzo_litro || 0);
    const iva = parseFloat(reading.meter?.aliquota_iva || reading.aliquota_iva || 22);
    if (!prezzo) return null;
    const gas = consumoLitri * prezzo;
    return gas + gas * (iva / 100);
  }, [consumoLitri, reading]);

  const canModify = reading.stato !== 'fatturata' && reading.stato !== 'annullata';

  // Inline update mutation
  const inlineUpdateMutation = useMutation({
    mutationFn: ({ id, data }) => meterReadingsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mobile-session-detail'] });
      queryClient.invalidateQueries({ queryKey: ['mobile-sessions'] });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Errore salvataggio', severity: 'error' });
      // Restore previous value
      setEditValue(prevValueRef.current);
    }
  });

  const displayValue = editValue !== null ? editValue : (
    reading.lettura_mc !== null && reading.lettura_mc !== undefined ? reading.lettura_mc : ''
  );

  const handleFocus = () => {
    const val = reading.lettura_mc !== null && reading.lettura_mc !== undefined ? reading.lettura_mc : '';
    prevValueRef.current = val;
    setEditValue(val);
  };

  const handleBlur = () => {
    if (editValue === null) return;
    const newVal = editValue === '' ? null : parseFloat(editValue);
    const oldVal = prevValueRef.current === '' ? null : parseFloat(prevValueRef.current);
    if (newVal !== oldVal && canModify) {
      inlineUpdateMutation.mutate({ id: reading.id, data: { lettura_mc: newVal } });
    }
    setEditValue(null);
  };

  const isFotoOnly = reading.tipo_lettura === 'foto' && (!reading.lettura_mc || parseFloat(reading.lettura_mc) === 0);

  // Compute local consumo for display when editing
  const localConsumoMc = useMemo(() => {
    const val = editValue !== null ? editValue : reading.lettura_mc;
    if (val === '' || val === null || val === undefined) return null;
    return parseFloat(val) - parseFloat(reading.lettura_precedente_mc || 0);
  }, [editValue, reading.lettura_mc, reading.lettura_precedente_mc]);

  const localIsAnomaly = localConsumoMc !== null && reading.consumo_medio_mc > 0 && localConsumoMc > reading.consumo_medio_mc * 2;
  const localIsNegative = localConsumoMc !== null && localConsumoMc < 0;

  // Distance from plant to reading GPS
  const gpsDistance = useMemo(() => {
    return getDistanceMeters(
      parseFloat(plantData?.latitude), parseFloat(plantData?.longitude),
      parseFloat(reading.gps_latitude), parseFloat(reading.gps_longitude)
    );
  }, [plantData?.latitude, plantData?.longitude, reading.gps_latitude, reading.gps_longitude]);

  // Inline tipo/fatturare handlers
  const handleTipoChange = (e) => {
    if (!canModify) return;
    inlineUpdateMutation.mutate({ id: reading.id, data: { tipo_lettura: e.target.value } });
  };
  const handleFatturareChange = (e) => {
    if (!canModify) return;
    inlineUpdateMutation.mutate({ id: reading.id, data: { da_fatturare: e.target.checked } });
  };

  // Meter history for collapse panel
  const { data: historyData, isLoading: historyLoading, error: historyError } = useQuery({
    queryKey: ['meter-history', reading.meter_id],
    queryFn: async () => {
      const res = await meterReadingsApi.getHistory(reading.meter_id, { limit: 20 });
      return res.data?.data || {};
    },
    enabled: expanded && !!reading.meter_id,
    staleTime: 60000
  });

  const historyReadings = historyData?.readings || [];
  const historyStats = historyData?.stats || {};

  return (
    <>
      <TableRow
        sx={{
          bgcolor: reading.stato === 'annullata' ? 'action.disabledBackground'
            : reading.stato === 'verificata' ? 'success.50'
            : isNegative ? 'error.50'
            : isAnomaly ? 'warning.50'
            : 'inherit',
          '&:hover': { bgcolor: 'action.hover' },
          '& > *': { borderBottom: expanded ? 'none' : undefined }
        }}
      >
        {/* Col 1: Expand */}
        <TableCell sx={{ width: 50 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={expanded ? "Chiudi dettagli" : "Mostra dettagli e storico"}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)} color={expanded ? 'primary' : 'default'}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
            {hasMultiple && (
              <Chip label={`+${olderReadings.length}`} size="small" sx={{ ml: 0.3, height: 20, fontSize: '0.7rem', minWidth: 28 }} color="info" />
            )}
          </Box>
        </TableCell>

        {/* Col 2: Progressivo */}
        <TableCell>
          <Typography variant="body2" fontWeight="bold">
            {reading.meter?.sotto_zona || reading.meter?.c_sotto_zo || reading.meter?.c_nu_conta || '-'}
          </Typography>
        </TableCell>

        {/* Col 3: Contatore */}
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {reading.meter?.meter_number || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {reading.meter?.api_code || '-'}
            </Typography>
          </Box>
        </TableCell>

        {/* Col 4: Cliente */}
        {!isMobile && (
          <TableCell>
            <Box>
              <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
                {reading.meter?.customer
                  ? `${reading.meter.customer.cognome || ''} ${reading.meter.customer.nome || ''}`.trim() || '-'
                  : '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {reading.meter?.customer?.cod_cli}{reading.meter?.customer?.localita ? ` - ${reading.meter.customer.localita}` : ''}
              </Typography>
            </Box>
          </TableCell>
        )}

        {/* Col 5: Insoluto */}
        {!isMobile && (
          <TableCell align="right">
            {reading.insoluto_cliente > 0 ? (
              <Chip
                label={formatCurrency(reading.insoluto_cliente)}
                size="small"
                color="error"
                variant="outlined"
              />
            ) : (
              <Chip label="OK" size="small" color="success" variant="outlined" />
            )}
          </TableCell>
        )}

        {/* Col 6: Ultima Lettura */}
        {!isMobile && (
          <TableCell align="right">
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {formatNumber(reading.meter?.ultima_lettura || reading.lettura_precedente_mc, 0)} MC
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(reading.meter?.data_ultima_lettura)}
              </Typography>
            </Box>
          </TableCell>
        )}

        {/* Col 7: Media Bimestrale */}
        {!isMobile && (
          <TableCell align="right">
            <Typography variant="body2" color="text.secondary">
              {formatNumber(reading.consumo_medio_mc, 2)} MC
            </Typography>
          </TableCell>
        )}

        {/* Col 8: Nuova Lettura (EDITABLE INLINE) + GPS badge */}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isFotoOnly ? (
              <Chip icon={<CameraAltIcon />} label="foto" size="small" color="warning" variant="outlined" />
            ) : (
              <TextField
                type="number"
                value={displayValue}
                onFocus={handleFocus}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleBlur}
                size="small"
                placeholder="MC"
                disabled={!canModify}
                inputProps={{ step: 0.01, min: 0 }}
                sx={{
                  width: 130,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: displayValue !== '' && displayValue !== null
                      ? (reading.da_fatturare !== false ? 'success.50' : 'warning.50')
                      : 'inherit',
                    '& fieldset': {
                      borderColor: localIsAnomaly ? 'warning.main' : localIsNegative ? 'error.main' : undefined,
                      borderWidth: localIsAnomaly || localIsNegative ? 2 : 1
                    }
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <>
                      {localIsAnomaly && (
                        <Tooltip title="Consumo anomalo (> 2x media)">
                          <WarningIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                      {localIsNegative && (
                        <Tooltip title="Consumo negativo! Verifica la lettura">
                          <WarningIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                      {displayValue !== '' && displayValue !== null && !localIsAnomaly && !localIsNegative && (
                        <CheckCircleIcon color="success" fontSize="small" />
                      )}
                      {inlineUpdateMutation.isPending && (
                        <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2 }} />
                      )}
                    </>
                  )
                }}
              />
            )}
            {gpsDistance !== null && (
              <Tooltip title={`Distanza dall'impianto: ${gpsDistance < 1000 ? Math.round(gpsDistance) + 'm' : (gpsDistance / 1000).toFixed(1) + 'km'}${reading.gps_accuracy ? ` (GPS ±${Math.round(reading.gps_accuracy)}m)` : ''}`}>
                <Chip
                  icon={<LocationOnIcon sx={{ fontSize: 14 }} />}
                  label={gpsDistance < 1000 ? `${Math.round(gpsDistance)}m` : `${(gpsDistance / 1000).toFixed(1)}km`}
                  size="small"
                  variant="outlined"
                  color={gpsDistance <= 50 ? 'success' : gpsDistance <= 200 ? 'warning' : 'error'}
                  sx={{ height: 22, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>

        {/* Col 9: Tipo lettura (Select inline) */}
        <TableCell sx={{ minWidth: 100 }}>
          <Select
            value={reading.tipo_lettura || 'reale'}
            onChange={handleTipoChange}
            size="small"
            disabled={!canModify}
            sx={{
              minWidth: 95,
              fontSize: '0.8rem',
              '& .MuiSelect-select': { py: 0.8 }
            }}
          >
            <MenuItem value="reale">Reale</MenuItem>
            <MenuItem value="stimata">Stimata</MenuItem>
            <MenuItem value="foto">Foto</MenuItem>
          </Select>
        </TableCell>

        {/* Col 10: Data/Ora */}
        <TableCell>
          <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
            {formatDateTime(reading.created_at)}
          </Typography>
        </TableCell>

        {/* Col 11: Foto */}
        <TableCell align="center">
          {photos.length > 0 ? (
            <IconButton size="small" onClick={() => onPhotoClick(photos, 0)}>
              <Badge badgeContent={photos.length} color="secondary">
                <CameraAltIcon fontSize="small" />
              </Badge>
            </IconButton>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>

        {/* Col 12: Da fatturare (Switch inline) */}
        {!isMobile && (
          <TableCell align="center">
            <Tooltip title={reading.da_fatturare !== false ? 'Verrà fatturata' : 'Solo registrazione, no fattura'}>
              <Switch
                checked={reading.da_fatturare ?? true}
                onChange={handleFatturareChange}
                size="small"
                disabled={!canModify}
                color={reading.da_fatturare !== false ? 'success' : 'warning'}
              />
            </Tooltip>
          </TableCell>
        )}

        {/* Col 13: Consumo */}
        <TableCell align="right">
          {consumoMc !== null && consumoMc !== undefined ? (
            <Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color={isNegative ? 'error.main' : isOneOrTwoMc ? '#f9a825' : isAnomaly ? 'warning.main' : 'success.main'}
              >
                {formatNumber(consumoMc, 2)} MC
              </Typography>
              {!isMobile && consumoLitri != null && (
                <Typography variant="caption" color="text.secondary">
                  {formatNumber(consumoLitri, 0)} L
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>

        {/* Col 14: Importo */}
        {!isMobile && (
          <TableCell align="right">
            <Typography variant="body2" fontWeight="bold" color="primary.main">
              {reading.importo_totale ? formatCurrency(reading.importo_totale) : importoStimato ? formatCurrency(importoStimato) : '-'}
            </Typography>
          </TableCell>
        )}

        {/* Col 15: Stato */}
        <TableCell>
          <Chip label={reading.stato} size="small" color={getStatoColor(reading.stato)} />
        </TableCell>

        {/* Col 16: Azioni */}
        <TableCell align="center">
          <Stack direction="row" spacing={0} justifyContent="center">
            {reading.stato === 'inserita' && (
              <Tooltip title="Convalida">
                <IconButton size="small" color="success" onClick={() => onValidate(reading.id)} disabled={validatePending}>
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canModify && (
              <Tooltip title="Modifica">
                <IconButton size="small" color="primary" onClick={() => onEdit(reading)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canModify && (
              <Tooltip title="Annulla">
                <IconButton size="small" color="error" onClick={() => onDelete(reading)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Expanded: details + photo thumbnails + older readings */}
      <TableRow>
        <TableCell colSpan={isMobile ? 10 : 16} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>

              {/* Quick stats chips */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<SpeedIcon />}
                  label={`Media: ${formatNumber(reading.consumo_medio_mc, 2)} MC/bimestre`}
                  size="small" color="info" variant="outlined"
                />
                {reading.readings_count > 0 && (
                  <Chip
                    icon={<HistoryIcon />}
                    label={`${reading.readings_count} letture fatturate`}
                    size="small" color="primary" variant="outlined"
                  />
                )}
                {reading.gps_accuracy && (
                  <Chip
                    icon={<LocationOnIcon />}
                    label={`GPS ±${Math.round(reading.gps_accuracy)}m`}
                    size="small" variant="outlined"
                    color={reading.gps_accuracy <= 10 ? 'success' : reading.gps_accuracy <= 50 ? 'warning' : 'error'}
                  />
                )}
              </Box>

              <Grid container spacing={2}>
                {/* Dettagli Rilevazione */}
                <Grid item xs={12} md={photos.length > 0 ? 6 : 12}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Dettagli Rilevazione
                  </Typography>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        Rilevata il: <strong>{formatDateTime(reading.created_at)}</strong>
                      </Typography>
                    </Stack>

                    {(reading.gps_latitude && reading.gps_longitude) && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOnIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          GPS: {parseFloat(reading.gps_latitude).toFixed(6)}, {parseFloat(reading.gps_longitude).toFixed(6)}
                          {reading.gps_accuracy && ` (±${Math.round(reading.gps_accuracy)}m)`}
                          {' — '}
                          <a
                            href={`https://maps.google.com/?q=${reading.gps_latitude},${reading.gps_longitude}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ color: '#1976d2' }}
                          >
                            Apri in Google Maps
                          </a>
                        </Typography>
                      </Stack>
                    )}

                    {reading.note && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Note letturista: <em>{reading.note}</em>
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                {/* Foto thumbnails */}
                {photos.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      <PhotoLibraryIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      Foto ({photos.length})
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                      {photos.map((photo, idx) => {
                        const pLat = plantData?.latitude;
                        const pLng = plantData?.longitude;
                        const dist = (photo.gps_latitude && photo.gps_longitude && pLat && pLng)
                          ? getDistanceMeters(parseFloat(photo.gps_latitude), parseFloat(photo.gps_longitude), parseFloat(pLat), parseFloat(pLng))
                          : null;
                        const distLabel = dist != null ? (dist < 1000 ? `${Math.round(dist)} m` : `${(dist / 1000).toFixed(1)} km`) : null;
                        return (
                          <Card
                            key={photo.id}
                            sx={{ width: 200, cursor: 'pointer', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}
                            onClick={() => onPhotoClick(photos, idx)}
                          >
                            <CardMedia
                              component="img"
                              height="130"
                              image={`${API_BASE}/meter-readings/photo/${photo.filename}`}
                              alt={photo.original_name || 'Foto'}
                              sx={{ objectFit: 'cover' }}
                              onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="130"><rect fill="%23eee" width="200" height="130"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999" font-size="12">Foto</text></svg>'; }}
                            />
                            <Box sx={{ px: 1, py: 0.5, bgcolor: 'grey.50' }}>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {formatDateTime(photo.created_at)}
                                </Typography>
                              </Stack>
                              {(photo.gps_latitude && photo.gps_longitude) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <LocationOnIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                    {parseFloat(photo.gps_latitude).toFixed(5)}, {parseFloat(photo.gps_longitude).toFixed(5)}
                                    {' '}
                                    <a href={`https://maps.google.com/?q=${photo.gps_latitude},${photo.gps_longitude}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', fontSize: 10 }} onClick={(e) => e.stopPropagation()}>Maps</a>
                                  </Typography>
                                  {distLabel && (
                                    <Chip label={`↔ ${distLabel}`} size="small" sx={{ height: 18, fontSize: 10, ml: 0.5 }} color={dist > 5000 ? 'warning' : 'default'} />
                                  )}
                                  {photo.gps_accuracy && (
                                    <Chip label={`GPS ±${Math.round(parseFloat(photo.gps_accuracy))}m`} size="small" sx={{ height: 18, fontSize: 10, ml: 0.5 }} color={parseFloat(photo.gps_accuracy) <= 10 ? 'success' : parseFloat(photo.gps_accuracy) <= 50 ? 'warning' : 'error'} />
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Grid>
                )}
              </Grid>

              {/* Storico fatture (MeterHistoryPanel) */}
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Storico fatture
                </Typography>
                {historyLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 2 }}>Caricamento storico...</Typography>
                  </Box>
                ) : historyError ? (
                  <Alert severity="error" sx={{ mb: 1 }}>Errore nel caricamento dello storico</Alert>
                ) : historyReadings.length === 0 ? (
                  <Alert severity="info">Nessuna lettura storica disponibile per questo contatore</Alert>
                ) : (
                  <>
                    {/* Statistiche rapide */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<HistoryIcon />}
                        label={`${historyStats.num_letture || 0} letture`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        icon={<SpeedIcon />}
                        label={`Media: ${formatNumber(historyStats.consumo_medio_mc, 2)} MC/bimestre`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                      <Chip
                        label={`Totale: ${formatNumber(historyStats.consumo_totale_mc, 0)} MC`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    {/* Tabella storico */}
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Data</TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Lettura MC</TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Consumo MC</TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Consumo Lt</TableCell>
                            <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Periodo</TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Importo</TableCell>
                            <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Fattura</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {historyReadings.map((r, idx) => (
                            <TableRow
                              key={r.id}
                              sx={{
                                bgcolor: idx === 0 ? 'primary.50' : 'inherit',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={idx === 0 ? 'bold' : 'normal'}>
                                  {formatDate(r.data_lettura)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="medium">
                                  {formatNumber(r.lettura_mc, 2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  color={r.is_anomaly ? 'warning.main' : 'text.primary'}
                                  fontWeight="medium"
                                >
                                  {formatNumber(r.consumo_mc, 2)}
                                  {r.is_anomaly && <WarningIcon sx={{ ml: 0.5, fontSize: 14, verticalAlign: 'middle' }} color="warning" />}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="text.secondary">
                                  {formatNumber(r.consumo_litri, 0)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {r.periodo_riferimento || `${r.anno}/${r.bimestre}`}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="medium" color="primary.main">
                                  {r.importo_totale ? formatCurrency(r.importo_totale) : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {r.invoice ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Chip
                                      label={`#${formatNuf(r.invoice.sezionale, r.invoice.nuf)}`}
                                      size="small"
                                      color={r.invoice.stato === 'pagata' ? 'success' : 'default'}
                                      variant="outlined"
                                    />
                                    {r.invoice.insoluto > 0 && (
                                      <Chip
                                        label={formatCurrency(r.invoice.insoluto)}
                                        size="small"
                                        color="error"
                                        variant="filled"
                                      />
                                    )}
                                  </Box>
                                ) : r.da_fatturare === false ? (
                                  <Chip
                                    label="non Fatturata"
                                    size="small"
                                    color="warning"
                                    variant="filled"
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ============================================================
// EDIT READING DIALOG
// ============================================================
const EditReadingDialog = ({ open, reading, onClose, onSave, saving, isMobile }) => {
  const [form, setForm] = useState({});

  // Reset form when reading changes  
  React.useEffect(() => {
    if (reading) {
      setForm({
        lettura_mc: reading.lettura_mc ?? '',
        tipo_lettura: reading.tipo_lettura || 'reale',
        da_fatturare: reading.da_fatturare ?? true,
        note: reading.note || ''
      });
    }
  }, [reading]);

  if (!reading) return null;

  const consumo = form.lettura_mc !== '' && form.lettura_mc !== null
    ? parseFloat(form.lettura_mc) - parseFloat(reading.lettura_precedente_mc || 0)
    : null;
  const consumoLitri = consumo !== null ? consumo * MC_TO_LITERS : null;

  const handleSave = () => {
    const data = {};
    if (form.lettura_mc !== '' && form.lettura_mc !== null) {
      data.lettura_mc = parseFloat(form.lettura_mc);
    }
    data.tipo_lettura = form.tipo_lettura;
    data.da_fatturare = form.da_fatturare;
    data.note = form.note;
    onSave(reading.id, data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        Modifica Lettura — Contatore {reading.meter?.meter_number}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Lettura MC"
            type="number"
            fullWidth
            value={form.lettura_mc}
            onChange={e => setForm(f => ({ ...f, lettura_mc: e.target.value }))}
            helperText={consumo !== null ? `Consumo: ${formatNumber(consumo, 2)} MC = ${formatNumber(consumoLitri, 0)} L` : ''}
            error={consumo !== null && consumo < 0}
            InputProps={{
              sx: consumo !== null ? {
                borderColor: consumo < 0 ? 'error.main' : consumo > (reading.consumo_medio_mc || 999999) * 2 ? 'warning.main' : 'success.main'
              } : {}
            }}
          />

          <FormControl fullWidth>
            <InputLabel>Tipo lettura</InputLabel>
            <Select
              value={form.tipo_lettura} label="Tipo lettura"
              onChange={e => setForm(f => ({ ...f, tipo_lettura: e.target.value }))}
            >
              <MenuItem value="reale">Reale</MenuItem>
              <MenuItem value="stimata">Stimata</MenuItem>
              <MenuItem value="foto">Foto</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={form.da_fatturare}
                onChange={e => setForm(f => ({ ...f, da_fatturare: e.target.checked }))}
                color="success"
              />
            }
            label="Da fatturare"
          />

          <TextField
            label="Note"
            multiline rows={2}
            fullWidth
            value={form.note}
            onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
          />

          {/* Riepilogo */}
          <Alert severity="info">
            Lettura precedente: <strong>{formatNumber(reading.lettura_precedente_mc, 2)} MC</strong>
            {' | '}Media consumo: <strong>{formatNumber(reading.consumo_medio_mc, 2)} MC</strong>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Annulla</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================
// PHOTO DIALOG (Lightbox)
// ============================================================
const PhotoDialog = ({ open, photos, initialIndex, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex]);

  if (!photos || photos.length === 0) return null;
  const photo = photos[Math.min(index, photos.length - 1)];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1">
          Foto {index + 1} di {photos.length}
          {photo?.original_name ? ` — ${photo.original_name}` : ''}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
        <img
          src={`${API_BASE}/meter-readings/photo/${photo?.filename}`}
          alt={photo?.original_name || 'Foto'}
          style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          onError={(e) => { e.target.alt = 'Impossibile caricare la foto'; }}
        />
      </DialogContent>
      {photos.length > 1 && (
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button disabled={index === 0} onClick={() => setIndex(i => i - 1)}>← Precedente</Button>
          <Button disabled={index >= photos.length - 1} onClick={() => setIndex(i => i + 1)}>Successiva →</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default MobileReadingsTab;
